import { useField } from "formik";
import React, { useCallback, useMemo, useEffect } from "react";
import Select, { ActionMeta, SingleValue } from "react-select";
import useBuyFormStore from "../stores/useBuyFormStore";
import { ISelectFieldProps } from "types/selectField.type";
import {useTranslation} from "react-i18next";

const colourStyles = {
    container: (styles, { isFocused }) => ({
        ...styles,
        width: "fit-content",
        minWidth: "100%",
        backgroundColor: isFocused ? "#fff" : "#F3F3F5",
        border: isFocused ? "1px solid #132E5C" : "1px solid rgba(0,0,0,0)",
        borderRadius: "4px",
        boxShadow: "none",
    }),
    menu: (styles) => ({
        ...styles,
        width: "100%",
        backgroundColor: "#F3F3F5",
        boxShadow: "none",
        outline: 0,
        marginTop: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        border: "1px solid #132E5C",
        padding: 0,
        margin: 0,
        borderBottomLeftRadius: "4px",
        borderBottomRightRadius: "4px",
    }),
    control: (styles, { isFocused }) => ({
        ...styles,
        backgroundColor: isFocused ? "#fff" : "#F3F3F5",
        border: "none",
        boxShadow: "none",
    }),
    placeholder: (styles) => ({ ...styles, color: "#6E8CB8" }),
    option: (styles, { isDisabled, isFocused }) => ({
        ...styles,
        marginTop: 0,
        backgroundColor: isDisabled ? "#DADADC" : isFocused ? "#F3F3F5" : "#fff",
        color: "#000000",
        borderBottom: "1px solid #CCCFD7",
        cursor: isDisabled ? "not-allowed" : "default",
    }),
};

const SelectField: React.FC<ISelectFieldProps> = ({
    name,
    options,
    isDisabled = false,
    isClearable = false,
    isSearchable = true,
    styles,
    className,
    onSelectionChange,
    ...props
}) => {
    const [field, meta, helpers] = useField<any>(name);

    const { requestedMinutes } = useBuyFormStore();
    const { t } = useTranslation();
    const isRequestedMinutes = name === "requestedMinutes";

    useEffect(() => {
        if (isRequestedMinutes && requestedMinutes && field.value !== requestedMinutes) {
            helpers.setValue(requestedMinutes);
        }
    }, [requestedMinutes, isRequestedMinutes, field.value, helpers]);

    const selectedOption = useMemo(() => {
        const fieldValue = isRequestedMinutes ? requestedMinutes : field.value;

        if (!fieldValue) {
            return null;
        }

        if (typeof fieldValue === "object" && fieldValue.value !== undefined) {
            const option = options.find((option) => option.value === fieldValue.value);
            return  option || options[0];
        }

        return options.find((option) => option.value === fieldValue) || null;
    }, [field.value, requestedMinutes, isRequestedMinutes, options]);

    const handleChange = useCallback(
        (newValue: SingleValue<any>, _actionMeta: ActionMeta<any>) => {
            if (!isRequestedMinutes) {
                helpers.setValue(newValue);

                if (meta.error && meta.touched) {
                    helpers.setError(undefined);
                }
            }

            if (onSelectionChange) {
                onSelectionChange(newValue);
            }
        },
        [helpers, meta.error, meta.touched, onSelectionChange, isRequestedMinutes]
    );

    return (
        <Select
            {...props}
            name={field.name}
            options={options}
            value={selectedOption}
            onChange={handleChange}
            onBlur={() => helpers.setTouched(true)}
            placeholder={props.placeholder ?? t("global.selectOption")}
            isDisabled={isDisabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            styles={styles || colourStyles}
            noOptionsMessage={({inputValue}) => t("global.loading")}
        />
    );
};

export default React.memo(SelectField);
